import {
  Archive,
  BookOpen,
  Download,
  Eye,
  FileText,
  Image,
  ImageOff,
  ImagePlus,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  ScanText,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const processingStatuses = new Set([
  "uploading",
  "queued",
  "inspecting",
  // Compatibilidad con versiones que estaban procesándose antes de retirar OCR.
  "ocr",
  "layout",
  "chunking",
  "indexing",
]);

const stageLabels = {
  uploading: "Subiendo",
  queued: "En cola",
  inspecting: "Analizando",
  ocr: "Analizando",
  layout: "Ordenando texto",
  chunking: "Fragmentando",
  indexing: "Indexando",
  ready: "Disponible",
  failed: "Falló",
  archived: "Archivada",
};

function sizeLabel(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function dateLabel(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(
    new Date(value),
  );
}

function downloadBlob({ blob, fileName }) {
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function UploadForm({
  document,
  onCancel,
  onSubmit,
  busy,
  uploadProgress,
  maxUploadBytes = 512 * 1024 * 1024,
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(document?.title || "");
  const [authors, setAuthors] = useState(document?.authors?.join("; ") || "");
  const [publisher, setPublisher] = useState(document?.publisher || "");
  const [editionLabel, setEditionLabel] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const fileError = file?.size > maxUploadBytes
    ? `El PDF pesa ${sizeLabel(file.size)} y el límite es ${sizeLabel(maxUploadBytes)}.`
    : "";
  const savingOnServer = busy && uploadProgress >= 100;

  const submit = (event) => {
    event.preventDefault();
    if (!file || file.size > maxUploadBytes) return;
    const formData = new FormData();
    formData.append("file", file);
    if (!document) {
      formData.append("title", title);
      formData.append("authors", authors);
      formData.append("publisher", publisher);
    }
    formData.append("editionLabel", editionLabel);
    formData.append("publishedAt", publishedAt);
    formData.append("language", "es");
    onSubmit(formData);
  };

  return (
    <form className="doctrine-upload-card" onSubmit={submit}>
      <div className="doctrine-card-heading">
        <div>
          <strong>{document ? `Nueva versión de ${document.title}` : "Nueva obra"}</strong>
          <span>El PDF debe tener texto seleccionable. El original se conserva sin modificaciones.</span>
        </div>
        {onCancel ? (
          <button type="button" className="doctrine-icon-button" onClick={onCancel} aria-label="Cerrar">
            <X size={16} />
          </button>
        ) : null}
      </div>
      {!document ? (
        <div className="doctrine-form-grid">
          <label>
            Título
            <input value={title} onChange={(event) => setTitle(event.target.value)} required />
          </label>
          <label>
            Autores <small>separados por punto y coma</small>
            <input value={authors} onChange={(event) => setAuthors(event.target.value)} required />
          </label>
          <label>
            Editorial
            <input value={publisher} onChange={(event) => setPublisher(event.target.value)} />
          </label>
        </div>
      ) : null}
      <div className="doctrine-form-grid doctrine-form-grid-version">
        <label>
          Edición
          <input value={editionLabel} onChange={(event) => setEditionLabel(event.target.value)} placeholder="Ej. 2ª edición" />
        </label>
        <label>
          Fecha de publicación
          <input type="date" value={publishedAt} onChange={(event) => setPublishedAt(event.target.value)} />
        </label>
        <label className="doctrine-file-field">
          PDF <small>hasta {sizeLabel(maxUploadBytes)}</small>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(event) => {
              const selected = event.target.files?.[0] || null;
              setFile(selected);
            }}
            required
          />
        </label>
      </div>
      {fileError ? <div className="support-access-error">{fileError}</div> : null}
      {busy ? (
        <div className={`doctrine-upload-progress${savingOnServer ? " doctrine-upload-progress-saving" : ""}`} role="status">
          <span style={{ width: `${uploadProgress}%` }} />
          <small>{savingOnServer ? "Guardando en servidor…" : `Subiendo… ${uploadProgress}%`}</small>
        </div>
      ) : null}
      <button className="doctrine-primary-button" disabled={busy || !file || Boolean(fileError)}>
        {busy ? <LoaderCircle className="doctrine-spin" size={16} /> : <Upload size={16} />}
        {busy
          ? savingOnServer ? "Guardando PDF" : "Subiendo PDF"
          : document ? "Subir versión" : "Subir e iniciar procesamiento"}
      </button>
    </form>
  );
}

function MetadataForm({ document, onCancel, onSubmit, busy }) {
  const [title, setTitle] = useState(document.title);
  const [authors, setAuthors] = useState(document.authors.join("; "));

  const submit = (event) => {
    event.preventDefault();
    onSubmit({ title, authors });
  };

  return (
    <form className="doctrine-upload-card" onSubmit={submit}>
      <div className="doctrine-card-heading">
        <div>
          <strong>Editar datos de la obra</strong>
          <span>El cambio se aplica al catálogo y a todas sus versiones.</span>
        </div>
        <button type="button" className="doctrine-icon-button" onClick={onCancel} aria-label="Cerrar">
          <X size={16} />
        </button>
      </div>
      <div className="doctrine-form-grid doctrine-form-grid-metadata">
        <label>
          Título
          <input value={title} onChange={(event) => setTitle(event.target.value)} required />
        </label>
        <label>
          Autores <small>separados por punto y coma</small>
          <input value={authors} onChange={(event) => setAuthors(event.target.value)} required />
        </label>
      </div>
      <button className="doctrine-primary-button" disabled={busy}>
        {busy ? <LoaderCircle className="doctrine-spin" size={16} /> : <Pencil size={16} />}
        Guardar cambios
      </button>
    </form>
  );
}

function AdminCoverPreview({ api, document }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let active = true;
    let objectUrl;
    setUrl(null);
    if (!document.cover?.available) return () => { active = false; };
    api.doctrineCover(document.id)
      .then(({ blob }) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, document.cover?.available, document.cover?.updatedAt, document.id]);

  return (
    <div className={`doctrine-admin-cover${url ? " has-image" : ""}`}>
      {url ? (
        <img src={url} alt={`Tapa de ${document.title}`} />
      ) : (
        <BookOpen aria-hidden="true" size={20} />
      )}
    </div>
  );
}

function CoverForm({
  document,
  onCancel,
  onSubmit,
  busy,
  uploadProgress,
  maxCoverBytes,
}) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const invalidType = file
    && file.type
    && !["image/jpeg", "image/png"].includes(file.type);
  const fileError = file?.size > maxCoverBytes
    ? `La imagen pesa ${sizeLabel(file.size)} y el límite es ${sizeLabel(maxCoverBytes)}.`
    : invalidType
      ? "La tapa debe ser una imagen PNG o JPG."
      : "";
  const savingOnServer = busy && uploadProgress >= 100;

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const submit = (event) => {
    event.preventDefault();
    if (!file || fileError) return;
    onSubmit(file);
  };

  return (
    <form className="doctrine-upload-card doctrine-cover-form" onSubmit={submit}>
      <div className="doctrine-card-heading">
        <div>
          <strong>{document.cover?.available ? "Reemplazar tapa" : "Cargar tapa"}</strong>
          <span>Usá una imagen vertical PNG o JPG. Se mostrará en el catálogo.</span>
        </div>
        <button type="button" className="doctrine-icon-button" onClick={onCancel} aria-label="Cerrar">
          <X size={16} />
        </button>
      </div>
      <div className="doctrine-cover-editor">
        <div className={`doctrine-cover-selection${previewUrl ? " has-image" : ""}`}>
          {previewUrl ? (
            <img src={previewUrl} alt="Vista previa de la tapa seleccionada" />
          ) : (
            <Image aria-hidden="true" size={24} />
          )}
        </div>
        <label className="doctrine-file-field">
          Imagen <small>PNG o JPG, hasta {sizeLabel(maxCoverBytes)}</small>
          <input
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            required
          />
        </label>
      </div>
      {fileError ? <div className="support-access-error">{fileError}</div> : null}
      {busy ? (
        <div className={`doctrine-upload-progress${savingOnServer ? " doctrine-upload-progress-saving" : ""}`} role="status">
          <span style={{ width: `${uploadProgress}%` }} />
          <small>{savingOnServer ? "Guardando en servidor…" : `Subiendo… ${uploadProgress}%`}</small>
        </div>
      ) : null}
      <button className="doctrine-primary-button" disabled={busy || !file || Boolean(fileError)}>
        {busy ? <LoaderCircle className="doctrine-spin" size={16} /> : <ImagePlus size={16} />}
        {busy ? "Guardando tapa" : document.cover?.available ? "Reemplazar tapa" : "Cargar tapa"}
      </button>
    </form>
  );
}

function Progress({ version }) {
  const progress = version.progress || { percent: 0 };
  return (
    <div className={`doctrine-status doctrine-status-${version.status}`}>
      <div>
        {processingStatuses.has(version.status) ? <LoaderCircle className="doctrine-spin" size={14} /> : null}
        <strong>{stageLabels[version.status] || version.status}</strong>
        <span>{progress.message}</span>
      </div>
      <div className="doctrine-stage-progress">
        <span style={{ width: `${progress.percent || 0}%` }} />
      </div>
    </div>
  );
}

function Preview({ api, preview, onClose }) {
  const [url, setUrl] = useState(null);
  const [textPage, setTextPage] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    let objectUrl;
    setLoading(true);
    setError("");
    if (preview.type === "original") {
      api.doctrineOriginal(preview.version.id)
        .then(({ blob }) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        })
        .catch((nextError) => active && setError(nextError.message))
        .finally(() => active && setLoading(false));
    } else {
      api.doctrineTextPage(preview.version.id, pageNumber)
        .then((result) => active && setTextPage(result.page))
        .catch((nextError) => active && setError(nextError.message))
        .finally(() => active && setLoading(false));
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, pageNumber, preview]);

  return (
    <div className="doctrine-preview-backdrop" role="dialog" aria-modal="true">
      <section className="doctrine-preview">
        <header>
          <div>
            <strong>{preview.type === "original" ? "PDF original" : "Texto seleccionable"}</strong>
            <span>{preview.document.title} · versión {preview.version.versionNumber}</span>
          </div>
          <button className="doctrine-icon-button" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </header>
        {preview.type === "text" ? (
          <nav className="doctrine-page-nav">
            <button disabled={pageNumber <= 1} onClick={() => setPageNumber((page) => page - 1)}>Anterior</button>
            <span>Página {pageNumber} de {preview.version.pageCount}</span>
            <button disabled={pageNumber >= preview.version.pageCount} onClick={() => setPageNumber((page) => page + 1)}>Siguiente</button>
          </nav>
        ) : null}
        <div className="doctrine-preview-body">
          {loading ? <div className="doctrine-preview-state"><LoaderCircle className="doctrine-spin" /> Cargando…</div> : null}
          {error ? <div className="support-access-error">{error}</div> : null}
          {!loading && !error && url ? <iframe title="PDF original" src={url} /> : null}
          {!loading && !error && preview.type === "text" ? (
            textPage ? (
              <article className="doctrine-text-page">
                <div className="doctrine-body-copy">{textPage.bodyText || "Esta página no contiene texto."}</div>
                {textPage.footnotesText ? (
                  <aside><strong>Notas al pie</strong>{textPage.footnotesText}</aside>
                ) : null}
              </article>
            ) : <div className="doctrine-preview-state">Esta página todavía no está procesada.</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function DoctrineAdminPage({ api }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [versionTarget, setVersionTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [coverTarget, setCoverTarget] = useState(null);
  const [savingCoverId, setSavingCoverId] = useState(null);
  const [coverProgress, setCoverProgress] = useState(0);
  const [removingCoverId, setRemovingCoverId] = useState(null);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [deletingVersionId, setDeletingVersionId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [maxUploadBytes, setMaxUploadBytes] = useState(512 * 1024 * 1024);
  const [maxCoverBytes, setMaxCoverBytes] = useState(8 * 1024 * 1024);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      setDocuments(await api.doctrineDocuments());
      setError("");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    let active = true;
    api.doctrineAdminAccess()
      .then((access) => {
        if (active && Number.isFinite(access.maxUploadBytes)) {
          setMaxUploadBytes(access.maxUploadBytes);
        }
        if (active && Number.isFinite(access.maxCoverBytes)) {
          setMaxCoverBytes(access.maxCoverBytes);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [api]);
  const hasProcessing = useMemo(() => documents.some(({ versions }) =>
    versions.some(({ status }) => processingStatuses.has(status))), [documents]);
  useEffect(() => {
    if (!hasProcessing && !uploading) return undefined;
    const timer = window.setInterval(() => void load({ quiet: true }), 2_000);
    return () => window.clearInterval(timer);
  }, [hasProcessing, load, uploading]);

  const submit = async (formData, document = null) => {
    setUploading(true);
    setUploadProgress(0);
    setError("");
    try {
      if (document) {
        await api.createDoctrineVersion(document.id, formData, setUploadProgress);
        setVersionTarget(null);
      } else {
        await api.createDoctrineDocument(formData, setUploadProgress);
      }
      await load({ quiet: true });
    } catch (nextError) {
      await load({ quiet: true });
      setError(nextError.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (promise) => {
    try {
      downloadBlob(await promise);
    } catch (nextError) {
      setError(nextError.message);
    }
  };

  const updateMetadata = async (document, input) => {
    setSavingMetadata(true);
    setError("");
    try {
      await api.updateDoctrineDocument(document.id, input);
      setEditTarget(null);
      await load({ quiet: true });
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setSavingMetadata(false);
    }
  };

  const removeVersion = async (document, version) => {
    const current = document.currentVersionId === version.id;
    const detail = current
      ? document.versions.some((candidate) =>
          candidate.id !== version.id && candidate.status === "ready")
        ? " Es la versión actual; se activará la versión disponible anterior."
        : " Es la única versión disponible; la obra quedará sin contenido activo."
      : "";
    if (!window.confirm(`¿Eliminar la versión ${version.versionNumber} de “${document.title}”?${detail}`)) return;
    setDeletingVersionId(version.id);
    setError("");
    try {
      await api.deleteDoctrineVersion(version.id);
      if (preview?.version.id === version.id) setPreview(null);
      await load({ quiet: true });
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setDeletingVersionId(null);
    }
  };

  const updateCover = async (document, file) => {
    setSavingCoverId(document.id);
    setCoverProgress(0);
    setError("");
    try {
      await api.updateDoctrineCover(document.id, file, setCoverProgress);
      setCoverTarget(null);
      await load({ quiet: true });
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setSavingCoverId(null);
    }
  };

  const removeCover = async (document) => {
    if (!window.confirm(`¿Quitar la tapa cargada de “${document.title}”? Se mostrará la tapa automática.`)) return;
    setRemovingCoverId(document.id);
    setError("");
    try {
      await api.deleteDoctrineCover(document.id);
      if (coverTarget?.id === document.id) setCoverTarget(null);
      await load({ quiet: true });
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setRemovingCoverId(null);
    }
  };

  const archive = async (document) => {
    if (!window.confirm(`¿Archivar “${document.title}”? Podrás conservar sus citas históricas.`)) return;
    try {
      await api.archiveDoctrineDocument(document.id);
      await load({ quiet: true });
    } catch (nextError) {
      setError(nextError.message);
    }
  };

  return (
    <div className="support-access-page doctrine-admin-page">
      <div className="support-access-heading doctrine-admin-heading">
        <span className="support-access-eyebrow">CONTENIDO PRIVADO</span>
        <h1>Administración de doctrina</h1>
        <p>Subí PDFs con texto seleccionable para conservarlos, versionarlos e indexarlos para Justiniano.</p>
      </div>
      {error ? <div className="support-access-error">{error}</div> : null}
      <UploadForm
        onSubmit={(formData) => submit(formData)}
        busy={uploading && !versionTarget}
        uploadProgress={uploadProgress}
        maxUploadBytes={maxUploadBytes}
      />

      <div className="doctrine-list-heading">
        <div><BookOpen size={18} /><strong>Obras</strong><span>{documents.length}</span></div>
        <button className="doctrine-secondary-button" onClick={() => load()} disabled={loading}>
          <RefreshCw size={14} className={loading ? "doctrine-spin" : ""} /> Actualizar
        </button>
      </div>
      {loading ? <div className="support-access-state"><LoaderCircle className="doctrine-spin" /> Cargando doctrina…</div> : null}
      {!loading && !documents.length ? <div className="support-access-state">Todavía no subiste ninguna obra.</div> : null}
      <div className="doctrine-document-list">
        {documents.map((document) => {
          const documentProcessing = document.versions.some(({ status }) =>
            processingStatuses.has(status));
          return (
          <section className="doctrine-document" key={document.id}>
            <header>
              <div className="doctrine-document-summary">
                <AdminCoverPreview api={api} document={document} />
                <div className="doctrine-document-copy">
                  <strong>{document.title}</strong>
                  <span>{document.authors.join(", ")}{document.publisher ? ` · ${document.publisher}` : ""}</span>
                  <small>
                    {document.cover?.available
                      ? `Tapa: ${document.cover.fileName}`
                      : "Sin imagen: Instrumenta mostrará una tapa automática"}
                  </small>
                </div>
              </div>
              <div className="doctrine-document-actions">
                <button
                  className="doctrine-secondary-button"
                  disabled={documentProcessing}
                  onClick={() => {
                    setEditTarget(document);
                    setVersionTarget(null);
                    setCoverTarget(null);
                  }}
                ><Pencil size={14} /> Editar</button>
                <button className="doctrine-secondary-button" onClick={() => {
                  setCoverTarget(document);
                  setEditTarget(null);
                  setVersionTarget(null);
                }}><ImagePlus size={14} /> {document.cover?.available ? "Reemplazar tapa" : "Cargar tapa"}</button>
                {document.cover?.available ? (
                  <button
                    className="doctrine-icon-button doctrine-danger"
                    disabled={removingCoverId === document.id}
                    onClick={() => removeCover(document)}
                    title="Quitar tapa"
                    aria-label={`Quitar tapa de ${document.title}`}
                  >
                    {removingCoverId === document.id
                      ? <LoaderCircle className="doctrine-spin" size={15} />
                      : <ImageOff size={15} />}
                  </button>
                ) : null}
                <button className="doctrine-secondary-button" onClick={() => {
                  setVersionTarget(document);
                  setEditTarget(null);
                  setCoverTarget(null);
                }}><Plus size={14} /> Nueva versión</button>
                <button className="doctrine-icon-button doctrine-danger" onClick={() => archive(document)} title="Archivar"><Archive size={15} /></button>
              </div>
            </header>
            {editTarget?.id === document.id ? (
              <MetadataForm
                document={document}
                onCancel={() => setEditTarget(null)}
                onSubmit={(input) => updateMetadata(document, input)}
                busy={savingMetadata}
              />
            ) : null}
            {versionTarget?.id === document.id ? (
              <UploadForm
                document={document}
                onCancel={() => setVersionTarget(null)}
                onSubmit={(formData) => submit(formData, document)}
                busy={uploading}
                uploadProgress={uploadProgress}
                maxUploadBytes={maxUploadBytes}
              />
            ) : null}
            {coverTarget?.id === document.id ? (
              <CoverForm
                document={document}
                onCancel={() => setCoverTarget(null)}
                onSubmit={(file) => updateCover(document, file)}
                busy={savingCoverId === document.id}
                uploadProgress={coverProgress}
                maxCoverBytes={maxCoverBytes}
              />
            ) : null}
            <div className="doctrine-version-list">
              {!document.versions.length ? (
                <div className="doctrine-empty-versions">
                  Esta obra no tiene versiones. Subí una nueva para volver a publicarla.
                </div>
              ) : null}
              {document.versions.map((version) => (
                <article className="doctrine-version" key={version.id}>
                  <div className="doctrine-version-main">
                    <FileText size={18} />
                    <div>
                      <strong>Versión {version.versionNumber}{version.editionLabel ? ` · ${version.editionLabel}` : ""}</strong>
                      <span>{version.originalFileName} · {sizeLabel(version.originalSize)}{version.publishedAt ? ` · ${dateLabel(version.publishedAt)}` : ""}</span>
                      {version.status === "ready" ? (
                        <small>{version.pageCount} páginas · {version.chunkCount} fragmentos{document.currentVersionId === version.id ? " · versión actual" : ""}</small>
                      ) : null}
                      {version.error ? <small className="doctrine-error-copy">{version.error.message}</small> : null}
                    </div>
                  </div>
                  <Progress version={version} />
                  <div className="doctrine-version-actions">
                    <button disabled={!version.originalAvailable} onClick={() => setPreview({ type: "original", document, version })}><Eye size={14} /> Original</button>
                    <button disabled={version.status !== "ready"} onClick={() => setPreview({ type: "text", document, version })}><ScanText size={14} /> Texto</button>
                    <button disabled={!version.originalAvailable} onClick={() => download(api.doctrineOriginal(version.id, true))}><Download size={14} /> PDF</button>
                    <button disabled={version.status !== "ready"} onClick={() => download(api.doctrineTextDownload(version.id))}><Download size={14} /> TXT</button>
                    <button
                      className="doctrine-danger"
                      disabled={processingStatuses.has(version.status) || deletingVersionId === version.id}
                      onClick={() => removeVersion(document, version)}
                    >
                      {deletingVersionId === version.id
                        ? <LoaderCircle className="doctrine-spin" size={14} />
                        : <Trash2 size={14} />}
                      Eliminar
                    </button>
                  </div>
                  {version.warnings?.length ? (
                    <details><summary>{version.warnings.length} advertencia(s)</summary>{version.warnings.map((warning) => <p key={warning}>{warning}</p>)}</details>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
          );
        })}
      </div>
      {preview ? <Preview api={api} preview={preview} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}
